// src/admin/hooks/useAdminAssets.js
import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import buildAdminAssetsQuery from './buildAdminAssetsQuery';
import { API_URL } from '../../config/env';

export default function useAdminAssets(queryString = '') {
  const isMounted = useIsMounted();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const fetchIt = useCallback(async () => {
    try {
      const qs = buildAdminAssetsQuery(queryString);
      const endpoint = qs ? `${API_URL}/v1/admin/assets?${qs}` : `${API_URL}/v1/admin/assets`;
      const res = await fetchWithAuth(endpoint);
      if (!res.ok) throw new Error(res.statusText);
      setData(await res.json());
      setError(null);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setData([]);
      setError(err);
    }
  }, [queryString, isMounted]);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  /**
   * Create a new asset
   * @param {Object} assetData - Asset data to create
   * @returns {Promise<Object>} - Created asset
   */
  const createAsset = useCallback(async (assetData) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/assets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ asset: assetData }),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.errors?.join(', ') || res.statusText);
    }
    const created = await res.json();
    // Refresh the list after creating
    await fetchIt();
    return created;
  }, [fetchIt]);

  /**
   * Update an existing asset
   * @param {number|string} id - Asset ID
   * @param {Object} assetData - Asset data to update
   * @returns {Promise<Object>} - Updated asset
   */
  const updateAsset = useCallback(async (id, assetData) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/assets/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ asset: assetData }),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.errors?.join(', ') || res.statusText);
    }
    const updated = await res.json();
    // Refresh the list after updating
    await fetchIt();
    return updated;
  }, [fetchIt]);

  return { data, error, refresh: fetchIt, createAsset, updateAsset };
}
